import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';

import { useAdminModeStore } from '../Stores/EditAdminStore';
import { ComponentProps } from './LoginPage';

// Import all login logic from service
import {
  getSSOProviderForEmail,
  loginWithEmailPassword,
  loginWithGoogle,
  loginWithSSO,
  handleRedirectResult,
  sendResetEmail,
  processPostLogin,
} from '../../sp-firebase/loginFB';

const LoginForm: React.FC<ComponentProps> = ({ setActiveComponent: _setActiveComponent }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { setIsAdmin } = useAdminModeStore();

  // Handle SSO/Google redirect results
  useEffect(() => {
    handleRedirectResult().then(async (result) => {
      if (result) {
        await onLoginSuccess(result.user.uid, result.authProvider);
      }
    });
  }, []);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  // Check if current email requires SSO
  const ssoProvider = getSSOProviderForEmail(email);

  // Post-login processing - called after successful auth
  const onLoginSuccess = async (uid: string, authProvider: 'email' | 'google' | 'sso' = 'email') => {
    const result = await processPostLogin(uid, authProvider);

    if (result.success) {
      sessionStorage.setItem('uid', result.userDocId!);
      setIsAdmin(result.isAdmin || false);
      console.log('✅ [LOGIN] Login complete, navigating...');
      navigate('/');
    } else {
      sessionStorage.removeItem('uid');
      toast.error(result.error || 'Login failed', { autoClose: 6000 });
    }
  };

  // Email/password login handler
  const handleLogin = async () => {
    const result = await loginWithEmailPassword(email, password);

    if (result.requiresSSO) {
      toast.error(result.error);
      return;
    }

    if (result.success && result.uid) {
      await onLoginSuccess(result.uid, 'email');
    } else {
      toast.error(`Failed to login: ${result.errorCode || result.error}`);
    }
  };

  // Google login handler
  const handleGoogleLogin = async () => {
    const result = await loginWithGoogle();

    if (result.success && result.uid) {
      // Popup completed - process login
      await onLoginSuccess(result.uid, 'google');
    } else if (!result.success) {
      toast.error(result.error || 'Failed to login with Google');
    }
    // If success but no uid, redirect was initiated - will be handled by useEffect
  };

  // SSO login handler
  const handleSSOLogin = () => {
    if (ssoProvider) {
      loginWithSSO(ssoProvider);
    }
  };

  // Forgot password handler
  const handleForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address first.');
      return;
    }
    const result = await sendResetEmail(email);
    if (result.success) {
      toast.success('Password reset email sent! Check your inbox.');
    } else {
      toast.error(result.error || 'Failed to send reset email.');
    }
  };
  return (
    <div className="w-full p-5">
      <div className="h-100 w-full align-items-center">
        <div className="">
          <div className="auth-form-wrapper">
            <p className="text-xl text-red">Log In</p>
            <p className="text-sm text-gray-500 mt-1 mb-3">
              Access is by invitation only. Contact your administrator if you need an account.
            </p>

            <div className="py-5">
              {/* Email input first - for domain detection */}
              <div className="pb-3">
                <input
                  className="form-control"
                  type="email"
                  placeholder="Work email"
                  value={email}
                  onChange={handleEmailChange}
                  onKeyUp={(e) => (e.key === 'Enter' && password ? handleLogin() : null)}
                  autoFocus
                />
              </div>

              {/* Dynamic SSO Button - shown when email domain matches */}
              {ssoProvider && (
                <div className="mb-3">
                  <button
                    className="btn btn-primary w-100"
                    style={ssoProvider.buttonStyle ? {
                      backgroundColor: ssoProvider.buttonStyle.bg,
                      color: ssoProvider.buttonStyle.text,
                      borderColor: ssoProvider.buttonStyle.bg
                    } : undefined}
                    onClick={handleSSOLogin}
                  >
                    🏢 Sign in with {ssoProvider.name}
                  </button>
                  <small className="text-muted d-block mt-1 text-center">
                    Your organization <strong>{ssoProvider.orgName}</strong> uses SSO for authentication
                  </small>
                </div>
              )}

              {/* Password & Google - hidden when SSO is required */}
              {!ssoProvider && (
                <>
                  <div className="pb-3">
                    <input
                      className="form-control"
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={handlePasswordChange}
                      onKeyUp={(e) => (e.key === 'Enter' ? handleLogin() : null)}
                    />
                  </div>

                  <div className="text-right" style={{ marginTop: '-18px', marginBottom: '16px' }}>
                    <a href="#" className="text-muted text-xs" onClick={handleForgotPassword}>
                      Forgot password?
                    </a>
                  </div>

                  <div className="mb-3">
                    <button
                      className="btn btn-outline-primary w-100"
                      onClick={handleLogin}
                    >
                      Log in
                    </button>
                  </div>

                  <div className="d-flex align-items-center my-3">
                    <div className="flex-grow-1 border-top"></div>
                    <span className="mx-2 text-muted text-sm">or</span>
                    <div className="flex-grow-1 border-top"></div>
                  </div>

                  <div className="mb-3">
                    <button className="btn btn-secondary btn-logo btn-google w-100" onClick={handleGoogleLogin}>
                      Continue with Google
                    </button>
                  </div>
                </>
              )}

              <ToastContainer />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
